import { group } from '@angular/animations';
import { cloneDeep as _cloneDeep } from 'lodash-es';

import {
  TagCategoryInfoFragment,
  TagCategoryTagInfoFragment, TaggableLogbookDetailsFragment, TagType, UserRole
} from 'generated/graphql';

import { roleIsGreaterEqThan } from 'auth/account-role-helper';
import { isNilOrEmpty, textContains } from 'helpers';
import { PropercasePipe } from 'shared/pipes/propercase.pipe';

export function getFieldLockClass(field: { locked: boolean }) {
  if (field.locked) {
    return 'fa-lock color--red';
  }

  return 'fa-unlock-alt color--green--002';
}

export function getFieldLockTooltip(field: { locked: boolean }) {
  return (field.locked) ? 'This field is locked' : 'This field is unlocked';
}

export function getFieldTypeIcon(field: { allowToCreateTagType: TagType }) {
  return getTagTypeIcon(field?.allowToCreateTagType);
}

export function getTagTypeIcon(tagType: TagType) {
  switch (tagType) {
    case TagType.Standard: {
      return 'fa-tag';
    }
    case TagType.Number: {
      return 'fa-hashtag';
    }
    case TagType.Date: {
      return 'fa-calendar';
    }
    case TagType.Address: {
      return 'fa-map-marker-alt';
    }
    case TagType.Notes: {
      return 'fa-sticky-note';
    }
    case TagType.Shift: {
      return 'fa-clock';
    }
    default:
      return 'fa-tag';
  }
}

export function getFieldTypeTooltip(field: { allowToCreateTagType: TagType }) {
  return 'Field type: ' + new PropercasePipe().transform(field.allowToCreateTagType);
}

export function isCategoryArchived(tagCategory: TagCategoryTagInfoFragment) {
  if (!tagCategory.hasAssociatedTags) {
    return false;
  }
  return !tagCategory.hasActiveTags;
}

/**
 *
 * @param groupsInput Input of tag groups
 * @param critieria Determines which categories should be removed, and then the the groups above
 */
export function filterGroupCategoriesByArchiveStatus<T extends { name: string; categories: Array<TagCategoryInfoFragment> }>(
  groupsInput: Array<T>,
  critieria: 'SomeArchived' | 'AllArchived' | 'Active' | 'ActiveOrEmpty' = 'ActiveOrEmpty'
) {
  const groups = _cloneDeep(groupsInput);

  groups.forEach(g => {
    g.categories = filterCategoriesByArchiveStatus(g.categories, critieria);
  });

  return groups;
}

/**
 *
 * @param groupsInput Input of tag groups
 * @param criteria Only return fields of given TagType
 */
 export function filterGroupCategoriesByTagType<T extends { name: string; categories: Array<TagCategoryInfoFragment> }>(
  groupsInput: Array<T>,
  criteria: Array<TagType> = null
) {
  if (isNilOrEmpty(criteria)) {
    return groupsInput;
  }

  const groups = _cloneDeep(groupsInput);

  groups.forEach(g => {
    g.categories = filterCategoriesByTagType(g.categories, criteria);
  });

  return groups;
}

/**
 *
 * @param groupsInput Input of tag groups
 * @param criteria Determines which categories should be removed, and then the the groups above
 */
export function filterCategoriesByArchiveStatus<T extends TagCategoryTagInfoFragment>(
  categories: Array<T>,
  criteria: 'SomeArchived' | 'AllArchived' | 'Active' | 'ActiveOrEmpty' = 'ActiveOrEmpty'
) {
  if (criteria === 'Active') {
    return categories.filter(c => c.hasActiveTags);
  }

  if (criteria === 'ActiveOrEmpty') {
    return categories.filter(c => !isCategoryArchived(c));
  }

  if (criteria === 'AllArchived') {
    return categories.filter(c => isCategoryArchived(c));
  }

  if (criteria === 'SomeArchived') {
    return categories.filter(c => c.hasArchivedTags);
  }

  return categories;
}


/**
 *
 * @param groupsInput Input of tag groups
 * @param critieria Determines which categories should be removed, and then the the groups above
 */
 export function filterCategoriesByTagType<T extends TagCategoryInfoFragment>(
  categories: Array<T>,
  criteria: Array<TagType> = null
) {
  if (isNilOrEmpty(criteria)) {
    return categories;
  }

  return categories.filter(cat => criteria.includes(cat.allowToCreateTagType));
}


/**
 * Filters to only groups which have a category or group
 * name which matches the input text.
 *
 * If entityTags is also provided, groups which match
 * an applied entityTag will also be included.
 *
 * @param value seach text
 * @param groupsInput array of tag groups
 * @param entityTags OPTIONAL: An Array of entityTags
 */
export function filterGroupsByText<T extends { name: string; categories: Array<{ name: string; id: string }> }>(
  value: string,
  groupsInput: Array<T>,
  entityTags: Array<TaggableLogbookDetailsFragment> = null
): T[] {

  const groups = _cloneDeep(groupsInput);

  value = value || '';
  groups.forEach(g => {
    g.categories = g.categories.filter(category => {
      const catGroupMatch = textContains(`${category.name} ${group.name}`, value);
      if (entityTags == null) {
        return catGroupMatch;
      }

      const hasMatchingTag = entityTags.filter(c => c.tag.category.id === category.id)
        .filter(c => textContains(c.tag.name, value))
        .length > 0;

      return hasMatchingTag || catGroupMatch;
    });
  });

  return groups.filter(g => g.categories.length > 0);
}

/**
 * Removes tag categories that are readonly
 * unless the user is a lead or admin
 *
 * @param groupsInput array of tag groups
 * @param userRole The current users role
 */
export function filterReadonlyCategories<T extends { name: string; categories: Array<{ name: string; id: string; readonly: boolean }> }>(
  userRole: UserRole,
  groupsInput: Array<T>,
): T[] {

  const groups = _cloneDeep(groupsInput);

  groups.forEach(g => {
    g.categories = g.categories.filter(category => {
      if (category.readonly && roleIsGreaterEqThan(userRole, UserRole.Administrator)) {
        return true;
      }

      return !category.readonly;
    });
  });

  return groups.filter(g => g.categories.length > 0);
}
